import { AxiosError } from 'axios';
import { QueryConfig, useQuery } from 'react-query';
import { __DEV__ } from '~src/config';
import { loadFileLog, loadFolderLog } from '~src/services/api/actionLogs';
import {
  DbFile,
  DbFileActionLog,
  DbFolder,
  DbFolderActionLog,
} from '~src/types/db';
import { useProjectId } from '../useProjectId';

export enum LOG_ITEM_TYPE {
  FILE = 'file',
  FOLDER = 'folder',
}

export interface ItemLogReturnType {
  logs: DbFileActionLog[] | DbFolderActionLog[];
  totalCount: number;
}

interface UseItemActionLogProps {
  itemType: LOG_ITEM_TYPE;
  itemId: DbFile['id'] | DbFolder['id'];
  before?: number;
  createdBy?: number;
  general: boolean;
  skip?: number;
  take?: number;
}

export const useItemActionLog = (
  {
    itemType,
    itemId,
    before,
    createdBy,
    general,
    skip,
    take,
  }: UseItemActionLogProps,
  queryConfig: QueryConfig<ItemLogReturnType, AxiosError> & {
    enabled?: boolean;
  }
) => {
  const projectId = useProjectId();

  const { enabled, ...options } = queryConfig;

  // if enabled = undefined then enabled = true
  const enabledOverwrite = enabled || enabled === undefined;

  const itemActionLogQuery = useQuery<ItemLogReturnType, AxiosError>(
    [
      itemType === LOG_ITEM_TYPE.FILE ? 'fileActionLog' : 'folderActionLog',
      {
        projectId: Number(projectId),
        before,
        general,
        skip,
        take,
        created_by: createdBy,
        [itemType === LOG_ITEM_TYPE.FILE
          ? 'versionedfileId'
          : 'folderId']: itemId,
      },
    ],
    itemType === LOG_ITEM_TYPE.FILE ? loadFileLog : loadFolderLog,
    {
      // refetch every 5 min
      refetchInterval: 5 * 60 * 1000,
      enabled: enabledOverwrite && projectId,
      refetchOnWindowFocus: !__DEV__,
      ...options,
    }
  );

  return itemActionLogQuery;
};
